import OpenAI from 'openai';
import { auth, db } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';

// Helper to check user subscription
async function getUserSubscription(): Promise<{ isPro: boolean; subscription: string }> {
  const user = auth.currentUser;
  if (!user) return { isPro: false, subscription: 'free' };
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const subscription = userDoc.data()?.subscription || 'free';
    const isPro = ['pro', 'premium', 'enterprise'].includes(subscription);
    return { isPro, subscription };
  } catch {
    return { isPro: false, subscription: 'free' };
  }
}

export interface AIResponse {
  success: boolean;
  data?: any;
  error?: string;
}

class AIServicePro {
  async analyzeImagePro(
    imageBase64: string,
    model: string,
    apiKey?: string
  ): Promise<AIResponse> {
    const { isPro } = await getUserSubscription();
    
    // For Pro users, use system API key from environment
    let effectiveApiKey = apiKey;
    if (isPro && !apiKey) {
      if (model === 'gpt4' || model === 'gpt4-vision') {
        effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
      } else if (model === 'claude') {
        effectiveApiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
      } else if (model === 'gemini') {
        effectiveApiKey = process.env.REACT_APP_GOOGLE_AI_API_KEY;
      }
    }
    
    if (!effectiveApiKey) {
      return { 
        success: false, 
        error: 'API key required. Please configure your API keys.' 
      };
    }
    
    try {
      // Use GPT-4 Vision for best results
      if (model === 'gpt4' || model === 'gpt4-vision') {
        return await this.analyzeImageWithGPT4Vision(imageBase64, effectiveApiKey);
      } else if (model === 'claude') {
        return await this.analyzeImageWithClaude(imageBase64, effectiveApiKey);
      } else if (model === 'gemini') {
        return await this.analyzeImageWithGemini(imageBase64, effectiveApiKey);
      }
      
      return {
        success: false,
        error: 'Model not supported for image analysis'
      };
    } catch (error) {
      console.error('Image analysis error:', error);
      return { success: false, error: `Image analysis failed: ${error}` };
    }
  }

  private async analyzeImageWithGPT4Vision(
    imageBase64: string,
    apiKey: string
  ): Promise<AIResponse> {
    try {
      const client = new OpenAI({
        apiKey,
        dangerouslyAllowBrowser: true
      });

      const response = await client.chat.completions.create({
        model: "gpt-4-vision-preview",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: `Analyze this image and provide:
                1. A detailed description
                2. Main objects or subjects
                3. Artistic style
                4. Mood and atmosphere
                5. Dominant colors
                6. A suggested AI prompt to recreate this image
                
                Format the response as JSON with keys: description, objects, style, mood, colors, prompt`
              },
              {
                type: "image_url",
                image_url: {
                  url: imageBase64
                }
              }
            ]
          }
        ],
        max_tokens: 1000
      });

      const content = response.choices[0]?.message?.content;
      if (!content) throw new Error('No response from GPT-4 Vision');

      try {
        const data = JSON.parse(content);
        return {
          success: true,
          data: {
            description: data.description || 'No description provided',
            objects: data.objects || [],
            style: data.style || 'unknown',
            mood: data.mood || 'unknown',
            colors: data.colors || [],
            prompt: data.prompt || 'No prompt generated'
          }
        };
      } catch (parseError) {
        // If JSON parsing fails, extract information from text
        return {
          success: true,
          data: {
            description: content,
            objects: [],
            style: 'analyzed',
            mood: 'analyzed',
            colors: [],
            prompt: content
          }
        };
      }
    } catch (error) {
      console.error('GPT-4 Vision error:', error);
      return { success: false, error: `GPT-4 Vision failed: ${error}` };
    }
  }

  private async analyzeImageWithClaude(
    imageBase64: string,
    apiKey: string
  ): Promise<AIResponse> {
    try {
      // Remove data URL prefix if present
      const base64Data = imageBase64.replace(/^data:image\/\w+;base64,/, '');
      
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          max_tokens: 1024,
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'image',
                  source: {
                    type: 'base64',
                    media_type: 'image/jpeg',
                    data: base64Data
                  }
                },
                {
                  type: 'text',
                  text: `Analyze this image and provide a JSON response with:
                  - description: detailed description
                  - objects: array of main objects
                  - style: artistic style
                  - mood: mood and atmosphere
                  - colors: dominant colors array
                  - prompt: AI prompt to recreate`
                }
              ]
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`Claude API error: ${response.status}`);
      }

      const result = await response.json();
      const content = result.content[0].text;
      
      try {
        const data = JSON.parse(content);
        return {
          success: true,
          data: {
            description: data.description || 'No description provided',
            objects: data.objects || [],
            style: data.style || 'unknown',
            mood: data.mood || 'unknown',
            colors: data.colors || [],
            prompt: data.prompt || 'No prompt generated'
          }
        };
      } catch {
        return {
          success: true,
          data: {
            description: content,
            objects: [],
            style: 'analyzed',
            mood: 'analyzed',
            colors: [],
            prompt: content
          }
        };
      }
    } catch (error) {
      console.error('Claude Vision error:', error);
      return { success: false, error: `Claude Vision failed: ${error}` };
    }
  }

  private async analyzeImageWithGemini(
    imageBase64: string,
    apiKey: string
  ): Promise<AIResponse> {
    try {
      const { GoogleGenerativeAI } = await import('@google/generative-ai');
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: "gemini-pro-vision" });

      // Remove data URL prefix
      const base64Data = imageBase64.replace(/^data:image\/\w+;base64,/, '');
      
      const prompt = `Analyze this image and provide a JSON response with these keys:
      - description: a detailed description
      - objects: array of main objects or subjects
      - style: the artistic style
      - mood: the mood and atmosphere
      - colors: array of dominant colors
      - prompt: a detailed AI prompt to recreate this image`;

      const imagePart = {
        inlineData: {
          data: base64Data,
          mimeType: "image/jpeg"
        }
      };

      const result = await model.generateContent([prompt, imagePart]);
      const response = await result.response;
      const text = response.text();
      
      try {
        const data = JSON.parse(text);
        return {
          success: true,
          data: {
            description: data.description || 'No description provided',
            objects: data.objects || [],
            style: data.style || 'unknown',
            mood: data.mood || 'unknown',
            colors: data.colors || [],
            prompt: data.prompt || 'No prompt generated'
          }
        };
      } catch {
        return {
          success: true,
          data: {
            description: text,
            objects: [],
            style: 'analyzed',
            mood: 'analyzed',
            colors: [],
            prompt: text
          }
        };
      }
    } catch (error) {
      console.error('Gemini Vision error:', error);
      return { success: false, error: `Gemini Vision failed: ${error}` };
    }
  }

  async enhancePromptPro(
    prompt: string,
    model: string,
    apiKey?: string
  ): Promise<AIResponse> {
    const { isPro } = await getUserSubscription();
    
    // For Pro users, use system API key from environment
    let effectiveApiKey = apiKey;
    if (isPro && !apiKey) {
      if (model === 'gpt4') {
        effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
      } else if (model === 'claude') {
        effectiveApiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
      } else if (model === 'gemini') {
        effectiveApiKey = process.env.REACT_APP_GOOGLE_AI_API_KEY;
      }
    }
    
    if (!effectiveApiKey) {
      return { 
        success: false, 
        error: 'API key required. Please configure your API keys.' 
      };
    }

    try {
      if (model === 'gpt4') {
        const client = new OpenAI({
          apiKey: effectiveApiKey,
          dangerouslyAllowBrowser: true
        });

        const response = await client.chat.completions.create({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: `You are an expert prompt engineer. Enhance the given prompt to be more detailed, creative, and effective for AI image generation.`
            },
            {
              role: "user",
              content: `Enhance this prompt: ${prompt}`
            }
          ],
          max_tokens: 500,
          temperature: 0.7
        });

        return {
          success: true,
          data: {
            enhanced: response.choices[0]?.message?.content || prompt
          }
        };
      }
      
      // Add support for other models as needed
      return {
        success: false,
        error: 'Model not supported for prompt enhancement'
      };
    } catch (error) {
      console.error('Prompt enhancement error:', error);
      return { success: false, error: `Enhancement failed: ${error}` };
    }
  }
}

const aiServicePro = new AIServicePro();
export default aiServicePro;